import { Box, Button, Flex, Stack } from '@chakra-ui/react';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuthorization } from '$/components/core/Authentication/hooks/useAuthorization';
import { SectionHeadline } from '$/components/core/Collection/SectionHeadline';
import { EmptyStateCard } from '$/components/core/EmptyStateCard';
import { StyledLink } from '$/components/core/StyledLink';
import { useIsMobile } from '$/hooks/useIsMobile';
import { DashboardSectionRow } from '$/pages/DashboardPages/components/DashboardSectionRow';
import { ProjectFolderItem } from '$/pages/DashboardPages/pages/Projects/components/ProjectFolderItem';
import { ProjectGridItems } from '$/pages/DashboardPages/pages/Projects/components/ProjectGridItems';
import { ProjectHeader } from '$/pages/DashboardPages/pages/Projects/components/ProjectHeader';
import { useProjectModal } from '$/pages/DashboardPages/pages/Projects/hooks/useProjectModal';
import { type ProjectFolder } from '$/services/usecases/projects/mapper/editorProject';
import { type ProjectOverviewItem } from '$/services/usecases/projects/mapper/projectOverview';

interface Props {
  projects: ProjectOverviewItem[];
  unfilteredProjects: ProjectOverviewItem[];
  folders: ProjectFolder[];
}

export const ProjectOverview: FC<Props> = ({
  folders,
  projects,
  unfilteredProjects,
}) => {
  const { t } = useTranslation();
  const { canCreateMoreProjects, hasPermission } = useAuthorization();
  const isMobile = useIsMobile();
  const onOpenProjectModal = useProjectModal.useOnOpen();

  const shownFolders = folders.map((folder) => ({
    ...folder,
    imageUrl: unfilteredProjects.find(
      (project) => project.parentId === folder.id,
    )?.thumbnailUrl,
  }));

  const isEmpty = folders.length === 0 && projects.length === 0;

  if (isEmpty) {
    return (
      <Stack gap='6' w='full' minH='calc(100dvh - 14rem)' p='7'>
        <ProjectHeader />
        <Flex align='center' justify='center' flex='1' minH='0'>
          <EmptyStateCard
            title={t('dashboard.projects.empty.title')}
            description={t('dashboard.projects.empty.description')}
            buttonText={t('dashboard.projects.empty.createProjectButton')}
            to='/editor'
            icon='projects'
          />
        </Flex>
      </Stack>
    );
  }

  return (
    <Stack gap='6' overflowY='auto' w='full' p='7'>
      <ProjectHeader />
      <SectionHeadline
        headline={`${t('dashboard.projects.projectFolders')} (${folders.length})`}
        customActionElement={
          <Button
            onClick={() => onOpenProjectModal('createProjectFolder')}
            variant='text'
          >
            {t('dashboard.projects.addProjectFolderButton')}
          </Button>
        }
      >
        <DashboardSectionRow>
          {shownFolders.map((folder) => (
            <ProjectFolderItem
              projectFolder={folder}
              key={folder.id}
              imageUrl={folder.imageUrl}
            />
          ))}
        </DashboardSectionRow>
      </SectionHeadline>
      {projects.length > 0 && (
        <SectionHeadline
          headline={`${t('dashboard.projects.title')} (${projects.length})`}
          customActionElement={
            <Box color='text'>
              <StyledLink
                color='textButton.color'
                to={'/editor'}
                disabled={
                  !hasPermission('Project_Create') ||
                  !canCreateMoreProjects() ||
                  isMobile
                }
              >
                {t('dashboard.welcome.newProjectButton')}
              </StyledLink>
            </Box>
          }
        >
          <DashboardSectionRow>
            <ProjectGridItems items={projects} hideNewProjectButton={true} />
          </DashboardSectionRow>
        </SectionHeadline>
      )}
    </Stack>
  );
};
